// src/modules/app-views/collaborators/pages/Init/modals/CollaboratorsParametersModal/tabs/DepartmentFormModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DepartmentFormModal = ({ open, onCancel, onSave, initialData, loading, mousePosition }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_department') : translateX('new_department');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          label: initialData.label || '',
        });
      } else {
        form.resetFields();
      }

      // Auto-focus on label field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onSave({
        label: (values.label || '').trim(),
      });
    } catch (error) {
      // Validation failed - form will show errors
      console.log('[DepartmentFormModal] Validation failed:', error);
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      mousePosition={mousePosition}
      getContainer={document.body}
      width={400}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onKeyDown={handleKeyDown}
        initialValues={{ label: '' }}
      >
        <Form.Item
          name="label"
          label={translateX('department')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input
            ref={inputRef}
            maxLength={50}
            style={{ height: 40 }}
            placeholder={translateX('department')}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DepartmentFormModal;
